/**
 * Currency conversion info when the user is converting with PayPal
 * - used when the user is sending to a different currency with a card
 * - this info is shown on the preview page
 */

import PropTypes from 'prop-types'

import React from 'react'
import glamorous from 'glamorous'
import {
  getDisclaimerMessage,
  getENDisclaimerMessage,
} from '../utils/currencyConversionFeeDisclaimer-utils'
import { isBFXCurrencyConversionTrmt } from '../../experiments/bfx-currency-conversion'
import enRTRStrings from '../../../../config/enRTRStrings'
import content from 'pp-react-l10n'
import { isDw2Repaint } from '../../experiments/dw2Repaint'
import { BodyText, CaptionText, v2grey600 } from '@paypalcorp/pp-react'
import { hasGuestFlowRevamp } from '../../experiments/guest'
let i18n = content('transfer/inc/currencyConversion')

const STYLES = {
  conversionContainer: {
    padding: '14px 0',
    textAlign: 'left',
    fontSize: 12,
    lineHeight: '21px',
    color: '#666666',
  },
  conversionRate: {
    fontSize: 15,
  },
  disclaimerMessage: {
    fontSize: 13,
  },
  rtrConversionText: {
    fontSize: '16px',
    color: '#2C2E2F',
    lineHeight: '24px',
  },
  rtrConversionENSubText: {
    fontSize: '14px',
    color: '#687173',
    lineHeight: '20px',
  },
  rtrDisclaimerText: {
    fontSize: '13px',
    color: '#2C2E2F',
    lineHeight: '18px',
  },
  rtrDisclaimerENSubText: {
    fontSize: '13px',
    color: '#687173',
    lineHeight: '18px',
  },
}

const ExchangeRate = glamorous.div('ppvx_text--md', {
  padding: '0 16px',
  color: '#000000',
})

const FeesInfo = glamorous.div(
  'ppvx_text--sm',
  {
    color: '#000000',
    padding: '3px 16px 0',
    marginBottom: '20px',
  },
  ({ showENSubTextRTR }) => {
    if (showENSubTextRTR) {
      return {
        marginBottom: '0px',
      }
    }
  }
)

const FeesInfoENSubTextRTR = glamorous.div('ppvx_text--sm', {
  color: '#000000',
  padding: '3px 16px 0',
  marginBottom: '20px',
  ...STYLES.rtrDisclaimerENSubText,
})

const ConversionContainer = glamorous.div({
  margin: hasGuestFlowRevamp() ? '0' : '0.5rem',
})

const ExchangeRateConversionContainer = glamorous.div({
  margin: hasGuestFlowRevamp() ? '0' : '0 0.5rem',
  borderBottom: '1px solid #dedede',
  paddingBottom: '1rem',
})

class CurrencyConversion extends React.Component {
  static propTypes = {
    selectedFundingOption: PropTypes.object.isRequired,
    currencyDecimal: PropTypes.string.isRequired,
    userCountry: PropTypes.string.isRequired,
    shouldShowCurrencyConversionLink: PropTypes.bool.isRequired,
    isPurchase: PropTypes.bool.isRequired,
    isGuest: PropTypes.bool,
    xbRate: PropTypes.string,
    isReview: PropTypes.bool,
    language: PropTypes.string,
    isFeeDisclaimerMessageDisabled: PropTypes.bool,
  }

  static defaultProps = {
    currencyDecimal: '.',
  }

  renderPPExchangeRate = (fundingOptionCurrencyCode, rate, currencyCode) => {
    return isDw2Repaint() || hasGuestFlowRevamp() ? (
      <CaptionText>
        {i18n.html('currencyConversion.paypalExchangeRate', {
          fundingOptionCurrencyCode,
          rate,
          currencyCode,
        })}
      </CaptionText>
    ) : (
      <ExchangeRate>
        {i18n.html('currencyConversion.paypalExchangeRate', {
          fundingOptionCurrencyCode,
          rate,
          currencyCode,
        })}
      </ExchangeRate>
    )
  }

  render() {
    let {
      currency_exchange = {},
      isRTR = false,
    } = this.props.selectedFundingOption
    let { language } = this.props
    let {
      exchange_funds_in: { currency_code: fundingOptionCurrencyCode } = {},
      exchange_funds_out: { currency_code: currencyCode } = {},
      exchange_rate = '',
    } = currency_exchange
    const rate = exchange_rate
      .toString()
      .replace('.', this.props.currencyDecimal)
    const convertWithPayPal = Object.keys(currency_exchange).length > 0
    const userCountry = this.props.userCountry
    const convertUsingCard = this.props.shouldShowCurrencyConversionLink
    const isPurchase = this.props.isPurchase
    const showENSubTextRTR =
      this.props.isReview === true &&
      isRTR === true &&
      language !== '' &&
      language !== 'en'

    const feeDisclaimerMessage = getDisclaimerMessage({
      convertWithPayPal,
      userCountry,
      convertUsingCard,
      isPurchase,
      isReview: this.props.isReview,
      isBetterRate: Number(exchange_rate) > Number(this.props.xbRate),
      isFeeDisclaimerMessageDisabled: this.props.isFeeDisclaimerMessageDisabled,
    })
    const feeDisclaimerMessageENSubText = getENDisclaimerMessage({
      convertWithPayPal,
      userCountry,
      convertUsingCard,
      isPurchase,
      isReview: this.props.isReview,
      isBetterRate: Number(exchange_rate) > Number(this.props.xbRate),
    })

    if (isBFXCurrencyConversionTrmt()) {
      return isDw2Repaint() || hasGuestFlowRevamp() ? (
        <ExchangeRateConversionContainer>
          {convertWithPayPal ? (
            this.renderPPExchangeRate(
              fundingOptionCurrencyCode,
              rate,
              currencyCode
            )
          ) : (
            <CaptionText>
              {i18n('currencyConversion.cardExchangeRate')}
            </CaptionText>
          )}
          {showENSubTextRTR && (
            <CaptionText style={{ color: v2grey600 }}>
              {convertWithPayPal
                ? enRTRStrings['currencyConversion.paypalExchangeRate']
                : enRTRStrings['currencyConversion.cardExchangeRate']}
            </CaptionText>
          )}
          <CaptionText>
            {this.props.isGuest ? i18n('guest.fee') : feeDisclaimerMessage}
          </CaptionText>
          {showENSubTextRTR && (
            <CaptionText style={{ color: v2grey600 }}>
              {feeDisclaimerMessageENSubText}
            </CaptionText>
          )}
        </ExchangeRateConversionContainer>
      ) : (
        <React.Fragment>
          {convertWithPayPal ? (
            this.renderPPExchangeRate(
              fundingOptionCurrencyCode,
              rate,
              currencyCode
            )
          ) : (
            <ExchangeRate>
              {i18n('currencyConversion.cardExchangeRate')}
            </ExchangeRate>
          )}

          {showENSubTextRTR && (
            <ExchangeRate
              style={showENSubTextRTR ? STYLES.rtrConversionENSubText : {}}
            >
              {convertWithPayPal
                ? enRTRStrings['currencyConversion.paypalExchangeRate']
                : enRTRStrings['currencyConversion.cardExchangeRate']}
            </ExchangeRate>
          )}
          <FeesInfo showENSubTextRTR={showENSubTextRTR}>
            {this.props.isGuest ? i18n('guest.fee') : feeDisclaimerMessage}
          </FeesInfo>
          {showENSubTextRTR && (
            <FeesInfoENSubTextRTR showENSubTextRTR={showENSubTextRTR}>
              {feeDisclaimerMessageENSubText}
            </FeesInfoENSubTextRTR>
          )}
          <div id="line" style={{ borderTop: '1px solid #dedede' }} />
        </React.Fragment>
      )
    }
    return isDw2Repaint() || hasGuestFlowRevamp() ? (
      <ConversionContainer>
        {convertWithPayPal && (
          <>
            <BodyText data-nemo="xb-conversion-rate">
              {i18n.html('content.currencyConversion', {
                fundingOptionCurrencyCode,
                rate,
                currencyCode,
              })}
            </BodyText>
            {showENSubTextRTR && (
              <CaptionText style={{ color: v2grey600 }}>
                {enRTRStrings['content.currencyConversion']}
              </CaptionText>
            )}
          </>
        )}
        <>
          <CaptionText>
            {this.props.isGuest ? i18n('guest.fee') : feeDisclaimerMessage}
          </CaptionText>
          {showENSubTextRTR && (
            <CaptionText style={{ color: v2grey600 }}>
              {feeDisclaimerMessageENSubText}
            </CaptionText>
          )}
        </>
      </ConversionContainer>
    ) : (
      <div style={STYLES.conversionContainer}>
        {convertWithPayPal && (
          <div className="row">
            <span
              className="col-xs-12 txtLeft"
              style={
                showENSubTextRTR
                  ? { ...STYLES.conversionRate, ...STYLES.rtrConversionText }
                  : STYLES.conversionRate
              }
              data-nemo="xb-conversion-rate"
            >
              {i18n.html('content.currencyConversion', {
                fundingOptionCurrencyCode,
                rate,
                currencyCode,
              })}
            </span>
            {showENSubTextRTR && (
              <span
                className="col-xs-12"
                style={showENSubTextRTR ? STYLES.rtrConversionENSubText : {}}
              >
                {enRTRStrings['content.currencyConversion']}
              </span>
            )}
          </div>
        )}
        <div className="row">
          <span
            className="col-xs-12 txtLeft"
            style={
              showENSubTextRTR
                ? { ...STYLES.disclaimerMessage, ...STYLES.rtrDisclaimerText }
                : STYLES.disclaimerMessage
            }
          >
            {this.props.isGuest ? i18n('guest.fee') : feeDisclaimerMessage}
          </span>
          {showENSubTextRTR && (
            <span
              className="col-xs-12 txtLeft"
              style={showENSubTextRTR ? STYLES.rtrDisclaimerENSubText : {}}
            >
              {feeDisclaimerMessageENSubText}
            </span>
          )}
        </div>
      </div>
    )
  }
}

export default CurrencyConversion
